import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import PainPointsSection from './components/PainPointsSection';
import InsightSection from './components/InsightSection';
import SolutionSection from './components/SolutionSection';
import ProcessSection from './components/ProcessSection';
import ServicesSection from './components/ServicesSection';
import ProjectsSection from './components/ProjectsSection';
import BookingSection from './components/BookingSection';
import NewsSection from './components/NewsSection';
import Footer from './components/Footer';
import { ScrollReveal } from '@/components/feature/ScrollAnimations';
import { usePageSEO } from '@/hooks/usePageSEO';

export default function HomePage() {
  usePageSEO({
    title: 'UPLIVING – Nhà Phố Cũ Sinh Dòng Tiền TP.HCM',
    description: 'UPLIVING chuyên tái sinh nhà phố cũ nội đô TP.HCM: sửa bán tăng 25–40% giá trị, xây căn hộ dịch vụ tăng 2–5× dòng tiền. Cam kết kết quả bằng hợp đồng. Bắt Mạch miễn phí.',
    keywords: 'nhà phố cũ tp hcm, căn hộ dịch vụ tp hcm, sửa bán nhà phố, xây cho thuê tp hcm, tái sinh nhà phố nội đô, tăng dòng tiền bất động sản',
    canonical: '/',
    ogTitle: 'UPLIVING – Nhà Phố Cũ Sinh Dòng Tiền TP.HCM',
    ogDescription: 'UPLIVING chuyên tái sinh nhà phố cũ nội đô TP.HCM: sửa bán tăng 25–40% giá trị, xây căn hộ dịch vụ tăng 2–5× dòng tiền. Cam kết kết quả bằng hợp đồng. Bắt Mạch miễn phí.',
    ogType: 'website',
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        '@id': 'https://upliving.vn/#business',
        name: 'UPLIVING',
        description: 'Công ty chuyên tái sinh nhà phố cũ nội đô TP.HCM: sửa bán tăng giá trị, xây căn hộ dịch vụ tăng dòng tiền, vận hành tài sản cho thuê.',
        url: 'https://upliving.vn',
        telephone: '+84931198679',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '19-21 Lô B Trường Sơn',
          addressLocality: 'Quận 10',
          addressRegion: 'TP.HCM',
          addressCountry: 'VN',
          postalCode: '700000',
        },
        geo: { '@type': 'GeoCoordinates', latitude: 10.7769, longitude: 106.7009 },
        areaServed: [
          { '@type': 'City', name: 'Ho Chi Minh City' },
          { '@type': 'AdministrativeArea', name: 'Quận 1' },
          { '@type': 'AdministrativeArea', name: 'Quận 3' },
          { '@type': 'AdministrativeArea', name: 'Quận 10' },
          { '@type': 'AdministrativeArea', name: 'Bình Thạnh' },
          { '@type': 'AdministrativeArea', name: 'Tân Bình' },
          { '@type': 'AdministrativeArea', name: 'Gò Vấp' },
        ],
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Dịch vụ tối ưu tài sản',
          itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Sửa Bán Nhà Phố Cũ', description: 'Cải tạo nhà phố cũ để tăng 25–40% giá bán, ROI 3–4× chi phí sửa' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Xây Căn Hộ Dịch Vụ', description: 'Thiết kế và xây dựng căn hộ dịch vụ TP.HCM, ROI 12–16%/năm' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Vận Hành Cho Thuê', description: 'Quản lý vận hành tài sản cho thuê, công suất lấp đầy >80%' } },
          ],
        },
        openingHoursSpecification: [
          { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '08:00', closes: '18:00' },
          { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '08:00', closes: '12:00' },
        ],
        sameAs: ['https://zalo.me/0931198679'],
        priceRange: '$$',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'UPLIVING',
        url: 'https://upliving.vn',
        potentialAction: { '@type': 'SearchAction', target: 'https://upliving.vn/du-an?q={search_term_string}', 'query-input': 'required name=search_term_string' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Nhà phố cũ có thể làm gì để tăng giá trị?',
            acceptedAnswer: { '@type': 'Answer', text: 'Nhà phố cũ tại TP.HCM có thể được tái sinh theo 2 hướng: (1) Sửa bán – cải tạo đúng chỗ để tăng 25–40% giá bán, ROI 3–4× chi phí sửa; (2) Xây căn hộ dịch vụ – chuyển đổi thành mô hình cho thuê tăng 2–5× dòng tiền hàng tháng. UPLIVING phân tích miễn phí để xác định hướng phù hợp nhất.' },
          },
          {
            '@type': 'Question',
            name: 'Căn hộ dịch vụ TP.HCM có ROI bao nhiêu?',
            acceptedAnswer: { '@type': 'Answer', text: 'Căn hộ dịch vụ (CHDV) tại TP.HCM thường đạt ROI 12–16%/năm, công suất lấp đầy 70–95% tùy khu vực. So với cho thuê nguyên căn truyền thống, mô hình CHDV tăng 2–5× dòng tiền hàng tháng trên cùng diện tích đất.' },
          },
          {
            '@type': 'Question',
            name: 'Sửa bán nhà phố mất bao lâu và chi phí bao nhiêu?',
            acceptedAnswer: { '@type': 'Answer', text: 'Dịch vụ Sửa – Cam Kết Bán của UPLIVING thường hoàn thành trong 28–45 ngày thi công, chi phí từ 200–500 triệu tùy quy mô. Trung bình bán được trong 60–90 ngày sau khi hoàn thiện. UPLIVING cam kết kết quả bằng hợp đồng – không đạt giá bán cam kết, hoàn tiền.' },
          },
          {
            '@type': 'Question',
            name: 'Xây cho thuê căn hộ dịch vụ cần bao nhiêu vốn?',
            acceptedAnswer: { '@type': 'Answer', text: 'Chi phí xây căn hộ dịch vụ tại TP.HCM dao động 2.000–2.800 triệu/tầng tùy khu vực. Với lô đất 160m² xây 5 tầng, tổng đầu tư khoảng 5–7 tỷ, thu nhập 40–60 triệu/tháng, hoàn vốn trong 8–10 năm. UPLIVING có công cụ tính thử dòng tiền miễn phí.' },
          },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen font-sans">
      <Navbar />
      <main>
        <HeroSection />
        <ScrollReveal>
          <PainPointsSection />
        </ScrollReveal>
        <ScrollReveal delay={100}>
          <InsightSection />
        </ScrollReveal>
        <ScrollReveal delay={100}>
          <SolutionSection />
        </ScrollReveal>
        <ScrollReveal delay={100}>
          <ProcessSection />
        </ScrollReveal>
        <ScrollReveal delay={100}>
          <ServicesSection />
        </ScrollReveal>
        <ScrollReveal delay={100}>
          <ProjectsSection />
        </ScrollReveal>
        <ScrollReveal delay={100}>
          <BookingSection />
        </ScrollReveal>
        <ScrollReveal delay={100}>
          <NewsSection />
        </ScrollReveal>
      </main>
      <Footer />
    </div>
  );
}
